import type { AdminProfile, CreateProviderBody } from "@/core/types";
import type { ApiContext, ApiRequest, ApiRequestWith } from "@/api/types";
import type { GetAllCseResponse } from "@/core/types/(api)/cse";
import type { GetAllCsmResponse } from "@/core/types/(api)/csm";
import type { GetAllPracticesResponse } from "@/core/types/(api)/practice";
import { getGroupAuthType } from "@/utils/authToken";
import { GROUP_AUTH_HEADER_NAME } from "@/api/constants";
import type { PracticeIdName } from "@/core/types/practice";
import type { UpdateAdminProfileVariables } from "@/portals/(admin)/(profile)/api/queryFunctions/updateAdminProfile";

import { httpAuth } from "../httpAuth";
import { httpAuthCookie } from "../httpAuthCookie";

// fields: "ids" trims the backend response to just { id, name } via a DynamoDB
// projection — response shape depends on which variant is requested.
// Note: this repo's tsconfig has `strict: false`, so a generic conditional
// type keyed on `"ids" | undefined` always resolves to the `"ids"` branch
// (undefined is a subtype of everything without strictNullChecks) — use
// overloads instead, which disambiguate on argument shape, not on `extends`.
type FetchAllPractices = {
  (args: {
    context?: ApiContext;
    searchParams: { fields: "ids" };
    signal?: AbortSignal;
  }): Promise<PracticeIdName[]>;
  (args?: { context?: ApiContext; signal?: AbortSignal }): Promise<GetAllPracticesResponse>;
};

type AdminRequestsTyped = {
  create_user: ApiRequestWith<void, { body: CreateProviderBody }>;
  fetch_admin_profile: ApiRequest<AdminProfile>;
  fetch_admin_profile_by_id: ApiRequestWith<AdminProfile, { searchParams: { id: string } }>;
  fetch_all_cse: ApiRequest<GetAllCseResponse>;
  fetch_all_csm: ApiRequest<GetAllCsmResponse>;
  fetch_all_practices: FetchAllPractices;
  update_profile: ApiRequestWith<void, { body: UpdateAdminProfileVariables }>;
};

export const adminRequestsTyped: AdminRequestsTyped = {
  create_user: function ({ body, context }) {
    return httpAuth
      .post("/v1/auth/signup", {
        context: { preventAllErrorToast: true, showLoader: false, ...context },
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: body,
      })
      .json<void>();
  },

  // Cookie-auth pilot (see .ai/auth-cookies/pilot-admin-profile.md)
  fetch_admin_profile: function ({ context } = {}) {
    return httpAuthCookie
      .post("/v1/admin/profile", {
        context: { preventAllErrorToast: true, showLoader: false, ...context },
      })
      .json<AdminProfile>();
  },

  // Cookie-auth pilot (see .ai/auth-cookies/pilot-admin-profile.md)
  fetch_admin_profile_by_id: function ({ context, searchParams }) {
    return httpAuthCookie
      .post("/v1/admin/profile", {
        context: { preventAllErrorToast: true, showLoader: false, ...context },
        searchParams,
      })
      .json<AdminProfile>();
  },

  // Cookie-auth pilot (see .ai/auth-cookies/pilot-admin-profile.md)
  fetch_all_cse: function ({ context } = {}) {
    return httpAuthCookie
      .post("/v1/admin/all_cse", {
        context: { preventAllErrorToast: true, showLoader: false, ...context },
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      })
      .json<GetAllCseResponse>();
  },

  // Cookie-auth pilot (see .ai/auth-cookies/pilot-admin-profile.md)
  fetch_all_csm: function ({ context } = {}) {
    return httpAuthCookie
      .post("/v1/admin/all_csm", {
        context: { preventAllErrorToast: true, showLoader: false, ...context },
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      })
      .json<GetAllCsmResponse>();
  },

  fetch_all_practices: function fetchAllPractices(
    args: { context?: ApiContext; searchParams?: { fields?: "ids" }; signal?: AbortSignal } = {},
  ) {
    const { context, searchParams } = args;

    return httpAuth
      .post("/v1/practice/all_practices", {
        context: { preventAllErrorToast: true, showLoader: false, ...context },
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        ...(searchParams?.fields && { searchParams: { fields: searchParams.fields } }),
      })
      .json<GetAllPracticesResponse | PracticeIdName[]>();
  } as AdminRequestsTyped["fetch_all_practices"],

  // Cookie-auth pilot (see .ai/auth-cookies/pilot-admin-profile.md)
  update_profile: function ({ body, context }) {
    return httpAuthCookie
      .post("/v1/admin/profile_update", {
        context: { preventAllErrorToast: true, showLoader: false, ...context },
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
        json: body,
      })
      .json<void>();
  },
};
